import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import { useMemo } from 'react';
import { Animated, ImageBackground } from 'react-native';
import { FadeIn, LinearTransition } from 'react-native-reanimated';
import {
    H5Regular,
    H6Emphasized,
    H6Regular,
    Image,
    isNonMobileWeb,
    isWeb,
    LabelRegular,
    PillButton,
    Shadows,
    XStack,
    YStack,
} from 'smoothie';

import type { UserMembership } from '../../user/types/User';
import type { Guide } from '../types/Guide';
import { isGuideDetailsModalOpenAtom } from './GuideDetailsModal';

interface GuideOverviewProps {
    guide?: Guide | null;
    membership?: UserMembership | null;
    onPressGuideDetails?: () => void;
}

const GuideOverview = ({ guide, membership }: GuideOverviewProps) => {
    const setShowGuideDetails = useSetAtom(isGuideDetailsModalOpenAtom);

    const formattedMembershipStartDate = useMemo(() => {
        return new Date(membership?.createdAt ?? '').toLocaleString('en-US', {
            month: 'long',
            year: 'numeric',
        });
    }, [membership?.createdAt]);

    if (!guide) {
        return null;
    }

    return isNonMobileWeb ? (
        <LargeYourGuide
            onPressGuideDetails={() => {
                setShowGuideDetails(true);
            }}
            guide={guide}
            formattedMembershipStartDate={formattedMembershipStartDate}
        />
    ) : (
        <SmallYourGuide
            onPressGuideDetails={() => {
                setShowGuideDetails(true);
            }}
            guide={guide}
            formattedMembershipStartDate={formattedMembershipStartDate}
        />
    );
};

interface GuideCardProps {
    guide?: Guide | null;
    formattedMembershipStartDate: string;
    onPressGuideDetails?: () => void;
}

const LargeYourGuide = ({
    onPressGuideDetails,
    guide,
    formattedMembershipStartDate,
}: GuideCardProps) => {
    return (
        <Animated.View
            {...(isWeb
                ? { entering: FadeIn.duration(500) }
                : { layout: LinearTransition.duration(500) })}
            style={{ flex: 1, width: '100%' }}
        >
            <H6Regular marginBottom={16}>Your Guide</H6Regular>
            <YStack
                flexGrow={1}
                borderRadius={20}
                height={356}
                width="auto"
                $sm={{ flex: 1 }}
                backgroundColor="$white"
                {...Shadows.SMALL}
                cursor="pointer"
                onPress={() => {
                    if (onPressGuideDetails) {
                        onPressGuideDetails();
                    } else {
                        router.push('/home/my-guide');
                    }
                }}
            >
                <ImageBackground
                    source={{ uri: guide?.profileImageUrl ?? '' }}
                    style={{ flex: 1 }}
                    imageStyle={{ borderRadius: 20 }}
                    resizeMode="cover"
                >
                    <LinearGradient
                        colors={['transparent', 'rgba(0, 0, 0, 0.6)']}
                        locations={[0.75, 1]}
                        style={{
                            height: '100%',
                            width: '100%',
                            borderRadius: 20,
                            position: 'absolute',
                        }}
                    />
                    <XStack
                        alignItems="flex-end"
                        justifyContent="space-between"
                        padding={16}
                        borderBottomLeftRadius={20}
                        borderBottomRightRadius={20}
                        gap={64}
                        marginTop="auto"
                    >
                        <YStack flexShrink={1}>
                            <H5Regular color="$white">
                                {guide?.firstName ?? ''}, CFP&reg;
                            </H5Regular>
                            <LabelRegular color="$white">
                                Your Guide since {formattedMembershipStartDate}
                            </LabelRegular>
                        </YStack>
                        {onPressGuideDetails != null && (
                            <XStack>
                                <PillButton
                                    pillProps={{
                                        display: 'none',
                                        $sm: { display: 'flex' },
                                    }}
                                    text="More info"
                                    onPress={onPressGuideDetails}
                                />
                            </XStack>
                        )}
                    </XStack>
                </ImageBackground>
            </YStack>
        </Animated.View>
    );
};

const SmallYourGuide = ({
    onPressGuideDetails,
    guide,
    formattedMembershipStartDate,
}: GuideCardProps) => {
    return (
        <XStack
            justifyContent="space-between"
            alignItems="center"
            padding={8}
            borderRadius={12}
            borderWidth={1}
            borderColor="$gray200"
            backgroundColor="$white"
            onPress={onPressGuideDetails}
            style={{ ...Shadows.MEDIUM }}
            $sm={{
                flex: 1,
            }}
        >
            <YStack
                $sm={{
                    height: '100%',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                }}
            >
                <Image
                    source={{ uri: guide?.profileImageUrl ?? '' }}
                    height={60}
                    width={60}
                    borderRadius={12}
                />
            </YStack>
            <YStack
                flex={1}
                height="100%"
                marginHorizontal={8}
                $sm={{
                    flex: 1,
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                }}
            >
                <H6Emphasized marginBottom={2}>
                    {guide?.firstName ?? ''}, CFP&reg;
                </H6Emphasized>
                <LabelRegular color="$gray1100">
                    {`Your Guide since ${formattedMembershipStartDate}`}
                </LabelRegular>
            </YStack>
            <YStack
                $sm={{
                    height: '100%',
                    justifyContent: 'flex-end',
                }}
            >
                {guide && onPressGuideDetails && (
                    <PillButton
                        text="More info"
                        onPress={onPressGuideDetails}
                    />
                )}
            </YStack>
        </XStack>
    );
};

export default GuideOverview;
